// Hook that loads highlight.js, loads any syntax highlighting files needed by
// passed language and then returns code as highlighted/formatted HTML string
import { useEffect, useState } from "react";

const NEWLINE_REGEX = /\n/g;

// Util to prefix newline (\n) characters in html source code with line number
function addSourceLineNumbers(html) {
  let line = 1;
  const newHtml = `<span class="lineNumber">${line}</span>${html}`;
  return newHtml.replace(NEWLINE_REGEX, () => {
    line += 1;
    return `\n<span class="lineNumber">${line}</span>`;
  });
}

export default function useHighlightedCode(language, code, hasLineNumbers) {
  // Local variable populated with hljs library once loaded
  const [hljs, setHljs] = useState();

  // Internal cache of languages to be used to determine wether to load and
  // register any given lanuage
  const [languages, setLanguages] = useState();

  const [highlightedCode, setHighlightedCode] = useState("");

  // Load lib and set inital languages
  useEffect(() => {
    import("highlight.js/lib/core").then(({ default: lib }) => {
      lib.configure({ tabReplace: "  " });
      setHljs(lib);
      setLanguages(lib.listLanguages());
    });
  }, []);

  // Load language and syntax highlight
  useEffect(() => {
    if (hljs && language && languages) {
      // Load language and register if language is not in list of currently
      // registered languages
      if (!languages.includes(language)) {
        import(
          /* webpackInclude: /(javascript|json|bash|rust).js$/ */
          `highlight.js/lib/languages/${language}`
        ).then(({ default: lang }) => {
          hljs.registerLanguage(language, lang);
          setLanguages(hljs.listLanguages());
        });
      } else {
        // Highlight code and set in state if language has been loaded and
        // registered
        let { value: html } = hljs.highlight(code, { language });

        // If specified, add line numbers
        if (hasLineNumbers) {
          html = addSourceLineNumbers(html);
        }

        setHighlightedCode(html);
      }
    }
  }, [hljs, language, languages, code, hasLineNumbers]);

  return [highlightedCode];
}
